import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

// eslint-disable-next-line import/no-cycle
import Header from './Header';
import ResultsSummary from './ResultsSummary';
import { smoothScrollIntoView } from './assemblies/shared/scroll';
import { ASSEMBLIES, CALC_COMPONENTS, GROUPS } from './constants';
import { getTranslatedHDD } from './data/location_and_hdd';
import { Provider, useTRC } from './store';
import { Container, Divider, Field, H3, LocationGrid, SummaryTable } from './styles';
import OcLabel from '../../ComponentLibrary/input-elements/oc-label';
import SelectDropdown from '../../ComponentLibrary/input-elements/oc-select-dropdown';
import Tab from '../../ComponentLibrary/oc-tab';
import TabList from '../../ComponentLibrary/oc-tabs';
import { connectLocation } from '../../location/LocationConnectors';

/* -----------------------------------------------------------------------------
 * Location block (province/region/HDD)
 * ---------------------------------------------------------------------------*/
const LocationBlock = ({ header, t }) => {
  const { state, setLocation } = useTRC();
  const { province, region } = state.location;

  const HDD = useMemo(() => getTranslatedHDD(t), [t]);
  const provinces = useMemo(() => Object.keys(HDD || {}).sort(), [HDD]);
  const regions = useMemo(() => (province ? Object.keys(HDD[province] || {}).sort() : []), [province, HDD]);
  const hdd = province && region ? HDD?.[province]?.[region] ?? null : null;

  return (
    <>
      {header?.copy && <Header copy={header.copy} />}

      <H3>{t('thermal_calculator.location')}</H3>
      <LocationGrid>
        {/* Left: Province/Region selectors */}
        <div>
          <Field>
            <OcLabel forId="province" text={t('thermal_calculator.results.province')} required />
            <SelectDropdown
              id="province"
              value={province}
              onChange={e => setLocation({ province: e.target.value, region: '', hdd: null })}
              style={{ marginTop: 0, padding: '0' }}
              options={[t('thermal_calculator.header.select_province'), ...provinces]}
            />
          </Field>

          <Field>
            <OcLabel forId="region" text={t('thermal_calculator.results.region')} required />
            <SelectDropdown
              id="region"
              value={region}
              onChange={e =>
                setLocation({ province, region: e.target.value, hdd: HDD?.[province]?.[e.target.value] ?? null })
              }
              disabled={!province}
              style={{ marginTop: 0, padding: '0' }}
              options={[t('thermal_calculator.header.select_region'), ...regions]}
            />
          </Field>
        </div>

        {/* Right: Summary card */}
        <div>
          <SummaryTable>
            <tbody>
              <tr>
                <th>{t('thermal_calculator.results.province')}</th>
                <td>{province || '—'}</td>
              </tr>
              <tr>
                <th>{t('thermal_calculator.results.region')}</th>
                <td>{region || '—'}</td>
              </tr>
              <tr>
                <th>{t('thermal_calculator.results.heating_degree_days')}</th>
                <td>{hdd ?? '—'}</td>
              </tr>
            </tbody>
          </SummaryTable>
        </div>
      </LocationGrid>
    </>
  );
};

// eslint-disable-next-line
LocationBlock.propTypes = { header: PropTypes.object, t: PropTypes.func.isRequired };

LocationBlock.defaultProps = { header: null };

/* -----------------------------------------------------------------------------
 * Tab panel: renders the calculators for the active tab
 * ---------------------------------------------------------------------------*/
const calcAnchorId = id => `trc-calc-${id}`;

const TabBody = ({ activeGroupId, onRequestModify, t }) => {
  const { state, setResults } = useTRC();
  const { province, region, hdd } = state.location;
  const hasLocation = !!(province && region);

  if (activeGroupId === 'summary') {
    return (
      <ResultsSummary
        province={province}
        region={region}
        hdd={hdd}
        hasLocation={hasLocation}
        onRequestModify={onRequestModify}
      />
    );
  }

  const group = GROUPS.find(g => g.id === activeGroupId);
  const items = group?.items || [];

  return (
    <div style={{ display: 'grid', gap: 40, marginTop: 10 }}>
      {items.map((item, index) => {
        const Calc = CALC_COMPONENTS[item.id];
        return (
          <div key={item.id} id={calcAnchorId(item.id)}>
            <span style={{ fontSize: 32, fontWeight: 700 }}>
              {t(`thermal_calculator.titles.${ASSEMBLIES[item.id]}`)}
            </span>
            <div style={{ marginTop: 32 }}>
              <Calc
                key={item.id}
                calcId={item.id}
                province={province}
                region={region}
                hdd={hdd}
                hasLocation={hasLocation}
                onResultsChange={(results, meta) =>
                  setResults({ calcId: item.id, results, meta: { title: item.label, ...meta } })
                }
              />
            </div>
            {index === 0 && <Divider margin="64px 0 24px 0" />} {/* divider after first item only */}
          </div>
        );
      })}
    </div>
  );
};

TabBody.propTypes = {
  activeGroupId: PropTypes.string.isRequired,
  onRequestModify: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

/* -----------------------------------------------------------------------------
 * Root
 * ---------------------------------------------------------------------------*/
export const ThermalResistanceCalculator = connectLocation(({ header, t }) => {
  // default to the first non-summary group if present
  const defaultGroup = (GROUPS.find(g => g.id !== 'summary') || GROUPS[0] || {}).id || 'summary';
  const [activeGroupId, setActiveGroupId] = useState(defaultGroup);

  const handleRequestModify = (calcId, groupId, { offset = 32 } = {}) => {
    setActiveGroupId(groupId);

    // Wait for the tab to render, then scroll smoothly using our helper.
    // This tries for ~300ms in case the panel mounts a tick later.
    let tries = 0;
    const maxTries = 6;

    const tryScroll = () => {
      const el = document.getElementById(calcAnchorId(calcId));
      if (el) {
        smoothScrollIntoView(el, { block: 'start', behavior: 'smooth', offset });
        // eslint-disable-next-line no-plusplus
      } else if (tries++ < maxTries) {
        requestAnimationFrame(tryScroll);
      }
    };

    requestAnimationFrame(tryScroll);
  };

  return (
    <Provider>
      <Container>
        <LocationBlock header={header} t={t} />

        <H3>{t('thermal_calculator.assembly_type')}</H3>
        <div>
          <TabList>
            {GROUPS.map(g => (
              <Tab
                key={g.id}
                label={t(`thermal_calculator.groups.${g.id}`)}
                active={activeGroupId === g.id}
                onClick={() => setActiveGroupId(g.id)}
              />
            ))}
          </TabList>
        </div>

        <TabBody activeGroupId={activeGroupId} onRequestModify={handleRequestModify} t={t} />
      </Container>
    </Provider>
  );
});

// eslint-disable-next-line react/require-default-props,react/forbid-prop-types
ThermalResistanceCalculator.propTypes = { header: PropTypes.object };
export default ThermalResistanceCalculator;
